/**
 * Rotary dial — the bell-rolodex centerpiece.
 * 10 contact "holes" arranged 36° apart at the canonical rotary positions.
 * Position 1 (top) = highest-warmth contact. Center: a hairline bell motif.
 * Tab/click a hole to surface the contact's details below the dial.
 */
import { store } from '../store.ts';
import { RELATIONSHIP_DISPLAY, relativeContact, daysSince } from '../types.ts';
import type { Person } from '../types.ts';

const RADIUS = 108;
const LABEL_RADIUS = 158;

interface DialPosition {
  index: number;
  display: number;
  angleDeg: number;
  x: number;
  y: number;
  labelX: number;
  labelY: number;
}

function computePositions(): DialPosition[] {
  return Array.from({ length: 10 }, (_, i) => {
    const angleDeg = -90 + i * 36;
    const rad = (angleDeg * Math.PI) / 180;
    return {
      index: i,
      display: i + 1,
      angleDeg,
      x: Math.cos(rad) * RADIUS,
      y: Math.sin(rad) * RADIUS,
      labelX: Math.cos(rad) * LABEL_RADIUS,
      labelY: Math.sin(rad) * LABEL_RADIUS,
    };
  });
}

function monogram(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '·';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase() || '·';
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

export class DialTop10 extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;
  #positions = computePositions();

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.inner_circle.slice(0, 10));
    });
  }

  disconnectedCallback(): void { this.#unsub?.(); }

  #renderEmpty(): void {
    this.#shadow.innerHTML = /* html */ `
<style>
  ${this.#styles()}
  .empty-card {
    position: relative;
    display: grid;
    gap: 16px;
    padding: 44px 28px 40px;
    border-radius: 10px;
    background:
      radial-gradient(ellipse at 30% 0%, oklch(34% 0.10 24 / 0.45), transparent 60%),
      radial-gradient(ellipse at 80% 100%, oklch(18% 0.06 24 / 0.55), transparent 65%),
      linear-gradient(165deg, var(--leather-up) 0%, var(--leather) 50%, var(--leather-edge) 100%);
    box-shadow:
      inset 0 0 0 1px oklch(38% 0.10 25 / 0.45),
      inset 0 -22px 38px oklch(14% 0.05 22 / 0.5),
      0 14px 32px -14px oklch(14% 0.06 22 / 0.5);
    overflow: hidden;
    isolation: isolate;
    text-align: center;
  }
  .empty-card::before {
    content: '';
    position: absolute;
    inset: 8px;
    border: 1px solid oklch(58% 0.13 75 / 0.35);
    border-radius: 6px;
    pointer-events: none;
  }
  .empty-card::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      repeating-linear-gradient(38deg,
        transparent 0 6px,
        oklch(38% 0.10 25 / 0.05) 6px 7px,
        transparent 7px 16px);
    mix-blend-mode: overlay;
    pointer-events: none;
  }
  .empty-card > * { position: relative; z-index: 1; }

  .empty-spindle {
    width: 56px; height: 56px;
    margin: 0 auto 4px;
    border-radius: 50%;
    background:
      radial-gradient(circle at 35% 30%,
        oklch(86% 0.10 80) 0%, oklch(68% 0.11 78) 40%, oklch(38% 0.09 70) 90%);
    box-shadow:
      inset 0 0 0 1px oklch(28% 0.07 70 / 0.7),
      inset 0 0 0 5px oklch(20% 0.07 22),
      inset 0 0 0 7px oklch(68% 0.11 78),
      0 0 0 2px oklch(18% 0.06 22 / 0.6),
      0 6px 12px -4px oklch(14% 0.06 22 / 0.6);
    position: relative;
  }
  .empty-spindle::after {
    content: '';
    position: absolute;
    inset: 22px;
    border-radius: 50%;
    background: oklch(18% 0.06 22);
    box-shadow: inset 0 0 0 1px oklch(58% 0.13 75 / 0.45);
  }

  .empty-issue {
    font-family: var(--plate);
    font-size: 10px;
    letter-spacing: 0.55em;
    color: oklch(78% 0.10 78 / 0.85);
    text-transform: uppercase;
    font-variant: small-caps;
  }
  .empty-title {
    font-family: var(--display);
    font-style: italic;
    font-weight: 700;
    font-size: clamp(26px, 7cqw, 34px);
    color: oklch(94% 0.05 75);
    letter-spacing: -0.012em;
    text-wrap: balance;
    line-height: 1.1;
  }
  .empty-quote {
    font-family: var(--display);
    font-style: italic;
    font-size: 17px;
    color: oklch(82% 0.07 70);
    text-wrap: balance;
    line-height: 1.4;
    max-width: 36ch;
    margin: 0 auto;
    padding: 0 8px;
  }
  .empty-quote::before { content: '\\201C'; margin-right: 4px; opacity: 0.7; }
  .empty-quote::after  { content: '\\201D'; margin-left: 2px;  opacity: 0.7; }
  .empty-attr {
    font-family: var(--mono);
    font-size: 9px;
    letter-spacing: 0.4em;
    text-transform: uppercase;
    color: oklch(78% 0.10 78 / 0.75);
    margin-top: 2px;
  }
  .empty-rule {
    width: 38px;
    height: 1px;
    background: var(--brass);
    opacity: 0.6;
    margin: 6px auto 0;
  }
  .empty-cta {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.32em;
    text-transform: uppercase;
    color: oklch(86% 0.06 78 / 0.85);
    margin-top: 8px;
  }
</style>
<section class="empty-card" aria-label="Empty rolodex waiting for first contact">
  <div class="empty-issue">Issue&nbsp;I &middot; MMXXVI</div>
  <div class="empty-spindle" aria-hidden="true"></div>
  <h3 class="empty-title">An empty rolodex,<br/>waiting for its first card.</h3>
  <p class="empty-quote">Tell me who answers when you call at midnight, and I will tell you who you are.</p>
  <div class="empty-attr">&mdash; the bell of mehfil</div>
  <div class="empty-rule" aria-hidden="true"></div>
  <p class="empty-cta">your top 50 is a portfolio &middot; emboss it slowly</p>
</section>
`;
  }

  #render(top: Person[]): void {
    if (!top.length) {
      this.#renderEmpty();
      return;
    }

    const dialHoles = this.#positions.map((p) => {
      const person = top[p.index];
      const hasPerson = person !== undefined;
      const glyph = hasPerson ? monogram(person.name) : '·';
      return /* html */ `
        <g class="hole ${hasPerson ? '' : 'hole-empty'}"
           data-position="${p.display}"
           data-index="${p.index}"
           tabindex="${hasPerson ? '0' : '-1'}">
          <circle class="hole-shadow" cx="${p.x}" cy="${p.y + 1.2}" r="21" />
          <circle class="hole-rim" cx="${p.x}" cy="${p.y}" r="21" />
          <circle class="hole-bg" cx="${p.x}" cy="${p.y}" r="18" />
          <text class="hole-num" x="${p.x}" y="${p.y - 1}" text-anchor="middle">${p.display}</text>
          <text class="hole-glyph" x="${p.x}" y="${p.y + 13}" text-anchor="middle">${escapeHtml(glyph)}</text>
        </g>`;
    }).join('');

    const dialLabels = this.#positions.map((p) => {
      const person = top[p.index];
      const name = person ? firstName(person.name) : '—';
      return /* html */ `
        <div class="label" style="--lx:${p.labelX.toFixed(2)}px;--ly:${p.labelY.toFixed(2)}px;" data-index="${p.index}">
          <span class="label-text">${escapeHtml(name)}</span>
        </div>`;
    }).join('');

    const listRows = top.map((p, i) => /* html */ `
      <li class="list-row" data-index="${i}" style="--warmth:${p.warmth_score}%;">
        <span class="row-num">${String(i + 1).padStart(2, '0')}</span>
        <span class="row-body">
          <span class="row-name">${escapeHtml(p.name)}</span>
          <span class="row-meta">${RELATIONSHIP_DISPLAY[p.relationship_kind]} · ${escapeHtml(p.city ?? '—')} · ${escapeHtml(relativeContact(p.last_contact_iso))}</span>
          <span class="row-bar"><span class="row-bar-fill"></span></span>
        </span>
        <span class="row-warmth">${p.warmth_score}</span>
      </li>`).join('');

    const firstPerson = top[0];
    const initialCaption = firstPerson
      ? this.#captionFor(firstPerson, 0)
      : '<span class="cap-eyebrow">tap a numeral to see the contact</span>';

    this.#shadow.innerHTML = /* html */ `
<style>
  ${this.#styles()}
</style>
<section class="rotary">
  <header class="rotary-head">
    <div class="eyebrow">Inner circle · top 10 · ranked by warmth</div>
    <h3 class="rotary-title">The Rotary</h3>
    <p class="rotary-lede">Numero uno sits at twelve o'clock. The bell at the centre answers when you forget.</p>
  </header>

  <div class="stage">
    <svg class="dial" viewBox="-180 -180 360 360" role="img" aria-label="Inner circle rotary dial showing top ten contacts">
      <defs>
        <radialGradient id="paper-grad" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stop-color="oklch(98% 0.02 75)" stop-opacity="1"/>
          <stop offset="70%" stop-color="oklch(95% 0.03 70)" stop-opacity="1"/>
          <stop offset="100%" stop-color="oklch(91% 0.04 65)" stop-opacity="1"/>
        </radialGradient>
        <radialGradient id="bell-grad" cx="0.5" cy="0.35" r="0.7">
          <stop offset="0%" stop-color="oklch(80% 0.09 80)" stop-opacity="1"/>
          <stop offset="100%" stop-color="oklch(58% 0.13 75)" stop-opacity="1"/>
        </radialGradient>
      </defs>

      <circle class="ring outer" cx="0" cy="0" r="172"/>
      <circle class="ring outer-glow" cx="0" cy="0" r="168"/>
      <circle class="ring brass" cx="0" cy="0" r="155"/>
      <circle class="ring dotted" cx="0" cy="0" r="140"/>
      <circle class="ring inner" cx="0" cy="0" r="78"/>

      <g class="stops">
        ${Array.from({ length: 10 }, (_, i) => {
          const a = (-90 + i * 36 + 18) * Math.PI / 180;
          const x1 = Math.cos(a) * 132;
          const y1 = Math.sin(a) * 132;
          const x2 = Math.cos(a) * 142;
          const y2 = Math.sin(a) * 142;
          return `<line class="stop" x1="${x1.toFixed(2)}" y1="${y1.toFixed(2)}" x2="${x2.toFixed(2)}" y2="${y2.toFixed(2)}"/>`;
        }).join('')}
      </g>

      <g class="holes">${dialHoles}</g>

      <g class="bell">
        <circle class="bell-base" cx="0" cy="0" r="56"/>
        <circle class="bell-hair" cx="0" cy="0" r="52"/>
        <g class="bell-glyph">
          <path d="M -16 -18 Q -16 -28 0 -28 Q 16 -28 16 -18 L 16 4 Q 16 10 12 12 L -12 12 Q -16 10 -16 4 Z"/>
          <line x1="-22" y1="13" x2="22" y2="13"/>
          <circle cx="0" cy="22" r="3"/>
          <line x1="0" y1="-32" x2="0" y2="-28"/>
        </g>
        <text class="bell-label" x="0" y="42" text-anchor="middle">MEHFIL</text>
      </g>
    </svg>

    <div class="labels">${dialLabels}</div>
  </div>

  <div class="caption" id="caption" aria-live="polite">${initialCaption}</div>

  <ol class="list">${listRows}</ol>
</section>
`;

    this.#wireEvents(top);
  }

  #captionFor(p: Person, idx: number): string {
    const days = daysSince(p.last_contact_iso);
    const ago = days == null ? 'no record' : (days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`);
    return /* html */ `
      <span class="cap-num">${String(idx + 1).padStart(2, '0')}</span>
      <span class="cap-body">
        <span class="cap-name">${escapeHtml(p.name)}</span>
        <span class="cap-meta">${RELATIONSHIP_DISPLAY[p.relationship_kind]} · ${escapeHtml(p.city ?? '—')} · last spoken ${escapeHtml(ago)}</span>
      </span>
      <span class="cap-warmth">${p.warmth_score}</span>
    `;
  }

  #wireEvents(top: Person[]): void {
    const captionEl = this.#shadow.getElementById('caption');
    if (!captionEl) return;

    let lastIdx = 0;
    const apply = (idx: number): void => {
      const person = top[idx];
      if (!person) return;
      captionEl.innerHTML = this.#captionFor(person, idx);
      this.#shadow.querySelectorAll('.list-row').forEach((el) => {
        el.classList.toggle('is-active', Number((el as HTMLElement).dataset.index) === idx);
      });
      this.#shadow.querySelectorAll('.label').forEach((el) => {
        el.classList.toggle('is-active', Number((el as HTMLElement).dataset.index) === idx);
      });
      this.#shadow.querySelectorAll('.hole').forEach((el) => {
        el.classList.toggle('is-active', Number((el as HTMLElement).dataset.index) === idx);
      });
    };

    const setFocus = (idx: number): void => {
      if (idx < 0 || idx >= top.length || !top[idx]) return;
      if (idx === lastIdx) return;
      const dir = idx > lastIdx ? 'fwd' : 'rev';
      this.#shadow.host.setAttribute('data-flick', dir);
      const prefersReduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
      if (!prefersReduced && typeof doc.startViewTransition === 'function') {
        doc.startViewTransition(() => apply(idx));
      } else {
        apply(idx);
      }
      lastIdx = idx;
    };

    this.#shadow.querySelectorAll('.hole').forEach((g) => {
      const el = g as SVGGElement;
      const idx = Number(el.dataset.index ?? -1);
      el.addEventListener('pointerenter', () => setFocus(idx));
      el.addEventListener('focusin', () => setFocus(idx));
      el.addEventListener('click', () => setFocus(idx));
    });

    this.#shadow.querySelectorAll('.list-row').forEach((row) => {
      const el = row as HTMLLIElement;
      const idx = Number(el.dataset.index ?? -1);
      el.addEventListener('pointerenter', () => setFocus(idx));
      el.addEventListener('click', () => setFocus(idx));
    });
  }

  #styles(): string {
    return /* css */ `
:host { display: block; }

.rotary {
  display: grid;
  gap: 22px;
  background: linear-gradient(170deg, var(--paper-card), var(--paper-up) 65%, var(--paper-deep));
  border: 1px solid var(--rule);
  border-radius: 10px;
  padding: 28px 18px 26px;
  overflow: hidden;
  position: relative;
  isolation: isolate;
}

.rotary::before {
  content: '';
  position: absolute;
  inset: 1px;
  border-radius: 9px;
  border: 1px solid color-mix(in oklch, var(--paper-card) 40%, transparent);
  pointer-events: none;
  z-index: 0;
}

.rotary-head { display: grid; gap: 6px; position: relative; z-index: 1; }

.eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--ink-faint);
}

.rotary-title {
  font-family: var(--display);
  font-style: italic;
  font-weight: 700;
  font-size: clamp(32px, 9cqw, 44px);
  letter-spacing: -0.02em;
  color: var(--maroon);
  text-wrap: balance;
}

.rotary-lede {
  font-family: var(--display);
  font-style: italic;
  font-size: 16px;
  color: var(--ink-dim);
  text-wrap: pretty;
  max-width: 38ch;
}

.stage {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  max-width: 360px;
  margin-inline: auto;
  z-index: 1;
}

.dial {
  width: 100%;
  height: 100%;
  display: block;
  filter: drop-shadow(0 14px 28px oklch(28% 0.12 22 / 0.18));
}

.ring { fill: none; }
.ring.outer { stroke: var(--brass-deep); stroke-width: 0.6; }
.ring.outer-glow { stroke: var(--brass); stroke-width: 0.4; opacity: 0.5; }
.ring.brass { stroke: var(--brass); stroke-width: 0.5; opacity: 0.7; }
.ring.dotted { stroke: var(--rule-strong); stroke-width: 0.5; stroke-dasharray: 1.4 3.6; opacity: 0.6; }
.ring.inner { fill: url(#paper-grad); stroke: var(--rule-strong); stroke-width: 0.4; }

.stops .stop {
  stroke: var(--brass-deep);
  stroke-width: 0.7;
  stroke-linecap: round;
  opacity: 0.55;
}

.holes {
  transform-origin: 0 0;
  transform-box: fill-box;
  animation: dial-spin-in 1100ms var(--spring) both;
}

@keyframes dial-spin-in {
  from { opacity: 0; transform: rotate(-32deg); }
  to   { opacity: 1; transform: rotate(0deg); }
}

.hole {
  cursor: pointer;
  outline: none;
  transition: transform 360ms var(--spring);
  transform-origin: center;
}

.hole-shadow {
  fill: oklch(28% 0.12 22 / 0.22);
  filter: blur(1.5px);
}

.hole-rim {
  fill: none;
  stroke: var(--maroon);
  stroke-width: 0.85;
  transition: stroke 220ms var(--snap), stroke-width 220ms var(--snap);
}

.hole-bg {
  fill: var(--paper);
  transition: fill 220ms var(--snap);
}

.hole-num {
  font-family: var(--display);
  font-weight: 700;
  font-size: 18px;
  font-style: italic;
  fill: var(--maroon);
  font-feature-settings: 'tnum' 1, 'ss01' 1;
  letter-spacing: -0.02em;
}

.hole-glyph {
  font-family: var(--mono);
  font-weight: 700;
  font-size: 7px;
  letter-spacing: 0.16em;
  fill: var(--ink-faint);
}

.hole:hover,
.hole:focus-visible,
.hole.is-active {
  transform: scale(1.085);
}

.hole:hover .hole-rim,
.hole:focus-visible .hole-rim,
.hole.is-active .hole-rim {
  stroke: var(--maroon-bright);
  stroke-width: 1.4;
}

.hole:hover .hole-bg,
.hole:focus-visible .hole-bg,
.hole.is-active .hole-bg {
  fill: color-mix(in oklch, var(--maroon) 18%, var(--paper));
}

.hole.hole-empty { cursor: default; opacity: 0.45; }
.hole.hole-empty:hover { transform: none; }
.hole.hole-empty .hole-rim { stroke-dasharray: 1 3; }

.bell-base {
  fill: url(#paper-grad);
  stroke: var(--brass-deep);
  stroke-width: 0.85;
}

.bell-hair {
  fill: none;
  stroke: var(--rule-strong);
  stroke-width: 0.4;
  stroke-dasharray: 1 3;
}

.bell-glyph path {
  fill: url(#bell-grad);
  stroke: var(--brass-deep);
  stroke-width: 1.1;
  stroke-linejoin: round;
}

.bell-glyph line {
  stroke: var(--brass-deep);
  stroke-width: 1;
  stroke-linecap: round;
}

.bell-glyph circle {
  fill: var(--brass-deep);
}

.bell-label {
  font-family: var(--mono);
  font-size: 6.5px;
  letter-spacing: 0.5em;
  fill: var(--ink-faint);
  font-weight: 700;
}

.labels {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(calc(-50% + var(--lx)), calc(-50% + var(--ly)));
  font-family: var(--mono);
  font-size: 8.5px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ink-faint);
  white-space: nowrap;
  transition: color 220ms var(--snap), transform 360ms var(--spring), letter-spacing 220ms var(--snap);
  text-align: center;
  animation: label-fade-in 1300ms var(--ease-out-quart) both;
}

.label-text {
  display: inline-block;
  padding: 2px 5px;
  border-radius: 999px;
  background: color-mix(in oklch, var(--paper-card) 70%, transparent);
}

.label.is-active {
  color: var(--maroon);
  letter-spacing: 0.32em;
}

.label.is-active .label-text {
  background: color-mix(in oklch, var(--maroon) 14%, var(--paper-card));
}

@keyframes label-fade-in {
  0%, 50% { opacity: 0; }
  100% { opacity: 1; }
}

.caption {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  background: var(--paper-up);
  border: 1px solid var(--rule);
  border-radius: 8px;
  border-left: 3px solid var(--maroon);
  view-transition-name: rotary-caption;
  min-height: 64px;
  position: relative;
  z-index: 1;
}

.cap-num {
  font-family: var(--display);
  font-style: italic;
  font-weight: 700;
  font-size: 32px;
  color: var(--maroon);
  letter-spacing: -0.02em;
  font-feature-settings: 'tnum' 1, 'ss01' 1, 'cv11' 1;
  line-height: 1;
}

.cap-body { display: grid; gap: 4px; min-width: 0; }

.cap-name {
  font-family: var(--display);
  font-weight: 600;
  font-size: 20px;
  color: var(--ink);
  letter-spacing: -0.01em;
  text-wrap: balance;
}

.cap-meta {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.06em;
  color: var(--ink-faint);
  text-transform: lowercase;
  text-wrap: pretty;
}

.cap-warmth {
  font-family: var(--display);
  font-weight: 700;
  font-size: 30px;
  color: var(--maroon);
  font-feature-settings: 'tnum' 1, 'ss01' 1;
  letter-spacing: -0.02em;
  line-height: 1;
}

.cap-eyebrow {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--ink-dim);
}

.list {
  display: grid;
  gap: 2px;
  background: var(--paper-up);
  border: 1px solid var(--rule);
  border-radius: 8px;
  overflow: hidden;
  list-style: none;
  position: relative;
  z-index: 1;
}

.list-row {
  display: grid;
  grid-template-columns: 36px 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid var(--rule);
  cursor: pointer;
  transition: background 220ms var(--snap);
  position: relative;
}

.list-row::after {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--maroon);
  transform: scaleY(0);
  transform-origin: center;
  transition: transform 220ms var(--spring);
}

.list-row:last-child { border-bottom: none; }

.list-row:hover,
.list-row.is-active {
  background: color-mix(in oklch, var(--peach) 14%, var(--paper-up));
}

.list-row.is-active::after { transform: scaleY(1); }

.row-num {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--maroon);
  letter-spacing: 0.12em;
  font-feature-settings: 'tnum' 1;
}

.row-body { display: grid; gap: 6px; min-width: 0; }

.row-name {
  font-family: var(--display);
  font-size: 19px;
  color: var(--ink);
  font-weight: 600;
  letter-spacing: -0.005em;
}

.row-meta {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-faint);
  letter-spacing: 0.05em;
  text-transform: lowercase;
}

.row-bar {
  height: 3px;
  background: oklch(94% 0.03 25);
  border-radius: 999px;
  overflow: hidden;
}

.row-bar-fill {
  display: block;
  height: 100%;
  width: var(--warmth);
  background: linear-gradient(90deg, var(--peach-deep), var(--peach));
  animation: bar-grow 720ms var(--snap) both;
}

@keyframes bar-grow {
  from { transform: scaleX(0); transform-origin: left; }
  to { transform: scaleX(1); }
}

.row-warmth {
  font-family: var(--display);
  font-size: 24px;
  font-weight: 700;
  color: var(--maroon);
  font-feature-settings: 'tnum' 1, 'ss01' 1;
  letter-spacing: -0.02em;
  line-height: 1;
  font-style: italic;
}

@media (prefers-reduced-motion: reduce) {
  .holes { animation: none; transform: rotate(0deg); }
  .label { animation: none; opacity: 1; }
  .row-bar-fill { animation: none; }
}
`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('dial-top-10', DialTop10);
